/**
 * Names for the server's ids where the table shows them : windows, actions, strikes, markers,
 * effects.
 */

import type {
    ActionKind,
    Effect,
    Marker,
    Mode,
    Strike,
    StrikeKind,
    Window,
} from '@/client/engine/api.ts'
import { gameResources } from '@/shared/registries.ts'

// A window's name
const WINDOWS: Partial<Record<Window, string>> = {
    unlock_abilities: 'Unlock phase',
    master_phase: 'Master phase',
    play_terms: 'Terms of the play',
    rider_terms: 'Paying an effect',
    phase_impulse: 'End of phase',
    card_as_played: 'As the card is played',
    minion_phase: 'Minion phase',
    action_as_announced: 'As the action is announced',
    action_block: 'Block attempts',
    action_after_resolution: 'After resolution',
    action_would_be_successful: 'Action would be successful',
    action_blocks_declined: 'Blocks declined',
    action_blocked: 'Action blocked',
    action_after_combat: 'After combat',
    torpor_block: 'Blocked leave torpor',
    rescue_cost: 'Rescue cost',
    entry_effect: 'Entering play',
    resolution_choice: 'Resolving an effect',
    block_toll: 'Block toll',
    distribution: 'Distributing blood',
    blood_hunt_polling: 'Blood hunt',
    referendum_terms: 'Referendum terms',
    referendum_polling: 'Referendum',
    discard_down: 'Discard down to hand size',
    action_terms: 'Terms of the action',
    library_search: 'Library search',
    action_exchange: 'Exchange',
    draw: 'Draw',
    bottom_cards: "Cards to the library's bottom",
    influence_transfers: 'Influence phase',
    discard_phase: 'Discard phase',
    combat_before_range: 'Combat: before range',
    combat_determine_range: 'Combat: range',
    combat_before_strikes: 'Combat: before strikes',
    combat_strike: 'Combat: strike',
    combat_before_resolution: 'Combat: before resolution',
    combat_prevention: 'Combat: damage prevention',
    combat_prevention_allocation: 'Combat: aggravated damage prevented',
    combat_additional_strikes: 'Combat: additional strikes',
    combat_press: 'Combat: press',
    combat_end_of_round: 'Combat: end of round',
}

// An action's name
const ACTIONS: Partial<Record<ActionKind, string>> = {
    bleed: 'bleed',
    hunt: 'hunt',
    leave_torpor: 'leave torpor',
    rescue: 'rescue',
    diablerie: 'diablerie',
    employ: 'employ',
    recruit: 'recruit',
    political: 'political action',
    card: 'action',
    burn_card: 'burn card',
    steal_card: 'steal card',
    equip: 'equip',
    equip_from: 'equip from a minion',
    equip_found: 'equip from the library',
    ability: 'action',
    become_anarch: 'become Anarch',
    red_list_hunt: 'enter combat with a Red List minion',
    power: 'get a Power',
    burn_imbued: 'burn an Imbued',
}

export const windowName = (w: Window) => WINDOWS[w] ?? w.replace(/_/g, ' ')
export const actionName = (a: ActionKind) => ACTIONS[a] ?? a.replace(/_/g, ' ')

// A strike's name
const STRIKES: Partial<Record<StrikeKind, string>> = {
    hand: 'hands',
    melee: 'weapon',
    ranged_burn_equipment: 'burn equipment',
    corrupt: 'corruption',
    card_effect: 'strike',
    unknown: 'strike',
}

// A declared strike as the combat panel shows it : its card ( a weapon ) or kind, then what it
// deals ( 'hands (3)', 'hands (1 agg)', 'Sniper Rifle (2 ranged)' )
export function strikeName(strike: Strike): string {
    const card = strike.card != null ? gameResources.cardbase[strike.card]?.name : undefined
    const name = card ?? STRIKES[strike.kind] ?? strike.kind.replace(/_/g, ' ')
    const deals = [
        strike.n != null ? `${strike.n}` : undefined,
        strike.aggravated ? 'agg' : undefined,
        strike.ranged ? 'ranged' : undefined,
    ].filter(part => part != undefined)
    return deals.length > 0 ? `${name} (${deals.join(' ')})` : name
}

// The effects told by their amount ( "+1 stealth", "−1 blood" )
const COUNTED = new Set([
    'stealth',
    'intercept',
    'bleed',
    'strength',
    'votes',
    'pool',
    'blood',
    'life',
    'damage',
    'prevent',
    'press',
    'maneuver',
    'draw',
    'discard',
    'transfers',
    'counters',
    'hand_size',
    'corruption',
])
// Effects the line says otherwise, or that say nothing more ( an action declared, a card kept )
const UNTOLD = new Set(['action', 'referendum', 'keep', 'role', 'cost'])

export function signed(n: number) {
    return n > 0 ? `+${n}` : `−${-n}`
}

export function effectsText(effects: Effect[] | null | undefined): string {
    return (effects ?? [])
        .filter(effect => !effect.cost && !UNTOLD.has(effect.kind))
        .map(effect => {
            const name = effect.kind.replace(/_/g, ' ')
            if (effect.kind == 'end_combat') {
                return 'ends combat'
            }
            if (COUNTED.has(effect.kind) && effect.n != null) {
                return `${effect.x ? `${signed(effect.n)}X` : signed(effect.n)} ${name}`
            }
            if (effect.kind == 'strike' && effect.strike) {
                return strikeName(effect.strike)
            }
            return name
        })
        .join(', ')
}

// A mode's printed tags : its Disciplines ( 'dom' basic, 'DOM' superior ), its card type ( 'COMBAT' )
export function modeTags(mode: Mode | null | undefined): string[] {
    const tags = (mode?.disciplines ?? []).map(d =>
        d.superior ? d.discipline.toUpperCase() : d.discipline,
    )
    if (mode?.card_type) {
        tags.push(mode.card_type.replace(/_/g, '').toUpperCase())
    }
    return tags
}

/**
 * A minion's markers as the table shows them : a counted kind ( hostage, nightmare, torture,
 * a lasting effect's counters ) as a chip, its count on it and its name on hover ; any other
 * mark ( disease, a contested title, expelled, a lasting effect without counters ) as a short
 * label. Corruption is SC's green counter, neither.
 */

// A counted marker : a round chip on the minion, one color per kind
export type EngineChip = { kind: ChipKind; n: number; name: string }
export type ChipKind = 'hostage' | 'nightmare' | 'torture' | 'effect'

// A marker's name, as the engine tells it ( its count in it : "2 hostage counters", "Expelled" )
function markerName(marker: Marker): string {
    const text = marker.text ?? marker.kind.replace(/_/g, ' ')
    return text.charAt(0).toUpperCase() + text.slice(1)
}

// A minion's markers, split : the chips, the labels
export function minionMarkers(markers: Marker[]): { chips: EngineChip[]; labels: string[] } {
    const chips: EngineChip[] = []
    const labels: string[] = []
    for (const marker of markers) {
        const n = marker.n ?? 0
        if (marker.kind == 'corruption') {
            continue
        }
        if (
            n > 0 &&
            (marker.kind == 'hostage' ||
                marker.kind == 'nightmare' ||
                marker.kind == 'torture' ||
                marker.kind == 'effect')
        ) {
            chips.push({ kind: marker.kind, n, name: markerName(marker) })
        } else {
            // A count with no chip of its own ( taint ) : in the label
            labels.push(markerName(marker))
        }
    }
    return { chips, labels }
}
